import Vue from 'vue';

/**
 * A store for application context toolbar - non persisting store
 */
const state = {
  buttons: [], // properties: id, name, icon, clickCommand, isActiveCommand, submenu
  anchor: () => {},
  anchorRect: null,
  openSubmenu: null,
  submenuPosition: {
    left: 0,
    top: 0,
  },
  submenuAnchor: () => {},
};

const getters = {
  /**
   * Returns whether the toolbar is empty (has no buttons)
   * @return  {Boolean}     Whether the toolbar has no buttons
   */
  getIsEmpty(state) {
    return state.buttons.length === 0;
  },

  /**
   * Returns the anchor callback function that positions the toolbar
   * @return  {Function}    The anchor function
   */
  getAnchor(state) {
    return state.anchor;
  },

  /**
   * Returns the anchor rectangle
   * @return    {Rect}      The anchor rect
   */
  getAnchorRect(state) {
    return state.anchorRect;
  },

  /**
   * Returns the toolbar buttons
   * @return  {Array}       The buttons
   */
  getButtons(state) {
    return state.buttons;
  },

  /**
   * Returns a toolbar button
   * @param   {String}  id    The button id
   * @return  {Object}        The button
   */
  getButtonById(state) {
    return id => {
      return state.buttons.find(button => button.id === id);
    };
  },

  /**
   * Returns whether the submenu is open
   * @return  {Boolean}       Whether the subemu is open
   */
  getOpenSubmenu(state) {
    return state.openSubmenu;
  },

  /**
   * Returns the submenu of a toolbar button
   * @param   {String} id   The button id
   * @return  {Object}      The submenu
   */
  getSubmenu(state, getters) {
    return id => {
      const button = getters.getButtonById(id);
      return button.submenu;
    };
  },

  /**
   * Returns the position of the submenu
   * @return   {Object}    The submenu position
   */
  getSubmenuPosition(state, getters) {
    return state.submenuPosition;
  },
};

const actions = {
  /**
   * Create a toolbar button
   * @param {String}    id   The button id
   * @param {String}    name The button name
   * @param {Function}  clickCommand  The command to be executed on click
   * @param {Function}  isActiveCommand The command that checks if it is active
   * @param {Function}  disabled  The command that checks if it is disabled
   * @param {Object}    submenu   The submenu that opens
   */
  addButton(
    context,
    { id, name, icon, clickCommand, isActiveCommand, disabled, submenu }
  ) {
    // First check if the button with that name already exists
    const index = context.state.buttons.findIndex(
      button => button.name === name
    );
    if (index !== -1) {
      context.commit('removeButton', { index });
    }
    context.commit('addButton', {
      id,
      name,
      icon,
      clickCommand,
      isActiveCommand,
      disabled,
      submenu,
    });
  },

  /**
   * Remove a toolbar button
   * @param {String}  name  The button name
   */
  removeButton(context, { name }) {
    const { buttons } = context.state;
    const index = buttons.findIndex(button => button.name === name);
    if (index !== -1) {
      context.commit('removeButton', { index });
    }
    // If no buttons remain remove the toolbar
    if (buttons.length === 0) {
      context.commit('removeToolbar');
    }
  },

  /**
   * Remove the toolbar
   */
  removeToolbar(context) {
    context.commit('removeToolbar');
  },

  /**
   * Set the anchor callback
   * @param {Function} anchor The anchor callback
   */
  setAnchor(context, { anchor }) {
    context.commit('setAnchor', { anchor });
  },

  /**
   * Open the submenu of a specific toolbar button
   * @param {String} buttonId The button id
   * @param {Function} anchor The anchor function
   */
  openSubmenu(context, { buttonId, anchor }) {
    context.commit('openSubmenu', { buttonId, anchor });
    context.dispatch('setSubmenuPosition');
  },

  /**
   * Set the position of the submenu
   */
  setSubmenuPosition(context) {
    if (!context.state.submenuAnchor) {
      return;
    }

    const rect = context.state.submenuAnchor();

    if (rect != null) {
      const position = {
        left: rect.left,
        top: rect.bottom + 10,
      };
      context.commit('setSubmenuPosition', { position });
    }
  },

  /**
   * Recalculate the anchor rectangle
   */
  recalcAnchorRect(context) {
    context.commit('recalcAnchorRect');
  },
};

const mutations = {
  /**
   * Clear the toolbar
   */
  empty(state) {
    state.buttons = [];
  },

  addButton(
    state,
    { id, name, icon, clickCommand, isActiveCommand, disabled, submenu }
  ) {
    state.buttons.push({
      id,
      name,
      icon,
      clickCommand,
      isActiveCommand,
      disabled,
      submenu,
    });
  },

  removeButton(state, { index }) {
    state.buttons.splice(index, 1);
  },

  setAnchor(state, { anchor }) {
    state.anchor = anchor;
    state.anchorRect = state.anchor();
  },

  recalcAnchorRect(state) {
    if (state.anchor) {
      state.anchorRect = state.anchor();
    }
  },

  openSubmenu(state, { buttonId, anchor }) {
    state.openSubmenu = buttonId;
    state.submenuAnchor = anchor;
  },

  setSubmenuPosition(state, { position }) {
    state.submenuPosition = position;
  },

  removeToolbar(state) {
    state.buttons = [];
    state.anchor = () => {};
    state.anchorRect = null;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
